import { useCustom } from '@refinedev/core';
import { Card, Col, Row, Statistic, Typography } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { RevenueKpiStatsOut } from '@/api/generated/schemas';
import { formatLedger, formatPayable } from '@/utils/money';
import { formatDuration } from '@/utils/duration';
import { cleanFilters, type SettlementStatsFilters } from './filters';

type Props = { filters: SettlementStatsFilters };

export const SettlementKpiCards = ({ filters }: Props) => {
  const currentPeriod = dayjs().startOf('month').format('YYYY-MM-DD');
  const previousPeriod = dayjs()
    .subtract(1, 'month')
    .startOf('month')
    .format('YYYY-MM-DD');

  const { query } = useCustom<RevenueKpiStatsOut>({
    url: 'settlement/charges_monthly/stats/kpi',
    method: 'get',
    config: {
      query: {
        current_period: currentPeriod,
        previous_period: previousPeriod,
        ...cleanFilters(filters),
      },
    },
  });

  const stats = query.data?.data;
  const momPct = stats?.mom_amount_pct;

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={12} lg={6}>
        <Card loading={query.isLoading}>
          <Statistic
            title="Należność (bieżący miesiąc)"
            value={stats?.current.payable_amount ?? '0'}
            formatter={(val) => formatPayable(val)}
            prefix={
              momPct != null ? (
                momPct >= 0 ? (
                  <ArrowUpOutlined style={{ color: '#3f8600' }} />
                ) : (
                  <ArrowDownOutlined style={{ color: '#cf1322' }} />
                )
              ) : undefined
            }
            valueStyle={{
              color: momPct != null && momPct < 0 ? '#cf1322' : undefined,
            }}
          />
          {/* The API computes the month-over-month change on the ledger and
              serves no payable twin, so the caption says which one it is
              rather than inventing a number that disagrees with the API. */}
          {momPct != null && (
            <Typography.Text
              type={momPct >= 0 ? 'success' : 'danger'}
              style={{ fontSize: 12, display: 'block', marginTop: 4 }}
            >
              {momPct >= 0 ? '+' : ''}
              {momPct.toFixed(1)}% vs poprzedni miesiąc (księga)
            </Typography.Text>
          )}
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, display: 'block', marginTop: 4 }}
          >
            Księga: {formatLedger(stats?.current.amount ?? '0')}
          </Typography.Text>
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card loading={query.isLoading}>
          <Statistic
            title="Czas odsłuchu"
            value={stats?.current.basis_seconds ?? 0}
            formatter={(val) => formatDuration(Number(val))}
          />
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, display: 'block', marginTop: 4 }}
          >
            Poprzednio: {formatDuration(stats?.previous.basis_seconds ?? 0)}
          </Typography.Text>
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card loading={query.isLoading}>
          <Statistic
            title="Aktywni wydawcy"
            value={stats?.current.publisher_count ?? 0}
          />
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, display: 'block', marginTop: 4 }}
          >
            Poprzednio: {stats?.previous.publisher_count ?? 0}
          </Typography.Text>
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card loading={query.isLoading}>
          <Statistic
            title="Aktywne produkty"
            value={stats?.current.product_count ?? 0}
          />
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, display: 'block', marginTop: 4 }}
          >
            Poprzednio: {stats?.previous.product_count ?? 0}
          </Typography.Text>
        </Card>
      </Col>
    </Row>
  );
};
